import { z } from 'zod';
import {
  MaRelativeMode,
  MaSlopeNormalization,
  MaTrendDirection,
  RelativePosition,
  LiquidationSignalMode,
  RsiMode,
  SignalType,
  ValueMode,
  ValueReactionDirection,
  ValueReactionEvent,
  ValueReactionScope
} from '@shared/types';

const OperatorSchema = z.enum(['AND', 'OR', 'NOT']);

export const RelativeSignalParamsSchema = z.object({
  position: z.nativeEnum(RelativePosition).default(RelativePosition.BELOW)
});
export type RelativeSignalParams = z.infer<typeof RelativeSignalParamsSchema>;

export const ValueRelativeSignalParamsSchema = z.object({
  mode: z.literal(ValueMode.RELATIVE),
  position: z.nativeEnum(RelativePosition).default(RelativePosition.BELOW)
});
export type ValueRelativeSignalParams = z.infer<
  typeof ValueRelativeSignalParamsSchema
>;

export const RsiAverageRelativeSignalParamsSchema = z.object({
  mode: z.literal(RsiMode.AVERAGE_RELATIVE),
  position: z.nativeEnum(RelativePosition).default(RelativePosition.BELOW),
  lookbackBars: z.number().int().min(1).default(3),
  minMaDelta: z.number().min(0).default(0)
});

export const RsiLevelSignalParamsSchema = z.object({
  mode: z.literal(RsiMode.LEVEL),
  position: z.nativeEnum(RelativePosition).default(RelativePosition.BELOW),
  level: z.number().min(0).max(100).default(50),
  confirmationCandles: z.number().int().min(1).default(1)
});

export const RsiSignalParamsSchema = z.discriminatedUnion('mode', [
  RsiAverageRelativeSignalParamsSchema,
  RsiLevelSignalParamsSchema
]);
export type RsiSignalParams = z.infer<typeof RsiSignalParamsSchema>;

export const MaTrendSignalParamsSchema = z.object({
  mode: z.literal(MaRelativeMode.TREND),
  period: z.number().int().min(1),
  direction: z
    .nativeEnum(MaTrendDirection)
    .default(MaTrendDirection.UP),
  lookbackBars: z.number().int().min(1).default(1)
});

export const MaPriceRelativeSignalParamsSchema = z.object({
  mode: z.literal(MaRelativeMode.PRICE_RELATIVE),
  period: z.number().int().min(1),
  position: z.nativeEnum(RelativePosition).default(RelativePosition.ABOVE)
});

export const MaPriceDeviationSignalParamsSchema = z.object({
  mode: z.literal(MaRelativeMode.PRICE_DEVIATION),
  period: z.number().int().min(1),
  position: z.nativeEnum(RelativePosition).default(RelativePosition.ABOVE),
  minDistancePct: z.number().min(0).max(100).default(0.1)
});

export const MaToMaSignalParamsSchema = z.object({
  mode: z.literal(MaRelativeMode.MA_TO_MA),
  period: z.number().int().min(1),
  referencePeriod: z.number().int().min(1),
  position: z.nativeEnum(RelativePosition).default(RelativePosition.BELOW)
});

export const MaSlopeSignalParamsSchema = z.object({
  mode: z.literal(MaRelativeMode.SLOPE),
  period: z.number().int().min(1),
  direction: z
    .nativeEnum(MaTrendDirection)
    .default(MaTrendDirection.UP),
  lookbackBars: z.number().int().min(1).default(3),
  minAngleDeg: z.number().min(0).max(89).default(5),
  normalization: z
    .nativeEnum(MaSlopeNormalization)
    .default(MaSlopeNormalization.PCT),
  atrPeriod: z.number().int().min(1).default(14)
});

export const MaReversalSignalParamsSchema = z.object({
  mode: z.literal(MaRelativeMode.REVERSAL),
  period: z.number().int().min(1),
  direction: z
    .nativeEnum(MaTrendDirection)
    .default(MaTrendDirection.UP),
  minAngleUpDeg: z.number().min(0).max(89).default(5),
  minAngleDownDeg: z.number().min(0).max(89).default(5),
  normalization: z
    .nativeEnum(MaSlopeNormalization)
    .default(MaSlopeNormalization.PCT),
  atrPeriod: z.number().int().min(1).default(14),
  keepSignalState: z.boolean().optional().default(true)
});

export const MaRelativeSignalParamsSchema = z.discriminatedUnion('mode', [
  MaTrendSignalParamsSchema,
  MaPriceRelativeSignalParamsSchema,
  MaPriceDeviationSignalParamsSchema,
  MaToMaSignalParamsSchema,
  MaSlopeSignalParamsSchema,
  MaReversalSignalParamsSchema
]);
export type MaRelativeSignalParams = z.infer<typeof MaRelativeSignalParamsSchema>;

export const ValueReactionSignalParamsSchema = z.object({
  mode: z.literal(ValueMode.REACTION),
  scope: z
    .nativeEnum(ValueReactionScope)
    .default(ValueReactionScope.LATEST),
  direction: z
    .nativeEnum(ValueReactionDirection)
    .default(ValueReactionDirection.BEARISH),
  event: z.nativeEnum(ValueReactionEvent).default(ValueReactionEvent.RETEST),
  touchTolerancePct: z.number().min(0).max(100).optional().default(0.1),
  breakMinDistancePct: z.number().min(0).max(100).optional().default(0),
  confirmationCandles: z.number().int().min(0).optional().default(3),
  preBreakCandles: z.number().int().min(1).optional().default(5),
  keepSignalState: z.boolean().optional().default(true),
  maxHistoricalLevels: z.number().int().min(1).optional().default(3),
  cooldownBarsPerLevel: z.number().int().min(0).optional().default(0)
});
export type ValueReactionSignalParams = z.infer<
  typeof ValueReactionSignalParamsSchema
>;

export const ValueSignalParamsSchema = z.discriminatedUnion('mode', [
  ValueRelativeSignalParamsSchema,
  ValueReactionSignalParamsSchema
]);
export type ValueSignalParams = z.infer<typeof ValueSignalParamsSchema>;

export const LiquidationSignalParamsSchema = z.object({
  mode: z
    .nativeEnum(LiquidationSignalMode)
    .default(LiquidationSignalMode.BEARISH),
  keepSignalState: z.boolean().optional().default(true)
});
export type LiquidationSignalParams = z.infer<
  typeof LiquidationSignalParamsSchema
>;

const RsiSignalConditionSchema = z.object({
  operator: OperatorSchema,
  value: z.literal(SignalType.RSI),
  enabled: z.boolean().optional().default(true),
  plot: z.boolean().optional(),
  params: RsiSignalParamsSchema.default({
    mode: RsiMode.LEVEL,
    position: RelativePosition.BELOW,
    level: 50,
    confirmationCandles: 1
  })
});

const MaSignalConditionSchema = z.object({
  operator: OperatorSchema,
  value: z.literal(SignalType.MA),
  enabled: z.boolean().optional().default(true),
  plot: z.boolean().optional(),
  params: MaRelativeSignalParamsSchema
});

const ValueSignalConditionSchema = z.object({
  operator: OperatorSchema,
  value: z.literal(SignalType.VALUE),
  enabled: z.boolean().optional().default(true),
  plot: z.boolean().optional(),
  params: ValueSignalParamsSchema.default({
    mode: ValueMode.RELATIVE,
    position: RelativePosition.BELOW
  })
});

const LiquidationSignalConditionSchema = z.object({
  operator: OperatorSchema,
  value: z.literal(SignalType.LIQUIDATION),
  enabled: z.boolean().optional().default(true),
  plot: z.boolean().optional(),
  params: LiquidationSignalParamsSchema.default({
    mode: LiquidationSignalMode.BEARISH,
    keepSignalState: true
  })
});

// Same pattern as indicators: each signal type owns its params shape.
const SignalConfluenceQuerySchema = z.union([
  RsiSignalConditionSchema,
  MaSignalConditionSchema,
  ValueSignalConditionSchema,
  LiquidationSignalConditionSchema
]);
export const SignalConditionInputSchema = SignalConfluenceQuerySchema;

const RsiSignalConfigSchema = z.object({
  type: z.literal(SignalType.RSI),
  params: RsiSignalParamsSchema.default({
    mode: RsiMode.LEVEL,
    position: RelativePosition.BELOW,
    level: 50,
    confirmationCandles: 1
  })
});

const MaSignalConfigSchema = z.object({
  type: z.literal(SignalType.MA),
  params: MaRelativeSignalParamsSchema
});

const ValueSignalConfigSchema = z.object({
  type: z.literal(SignalType.VALUE),
  params: ValueSignalParamsSchema.default({
    mode: ValueMode.RELATIVE,
    position: RelativePosition.BELOW
  })
});

const LiquidationSignalConfigSchema = z.object({
  type: z.literal(SignalType.LIQUIDATION),
  params: LiquidationSignalParamsSchema.default({
    mode: LiquidationSignalMode.BEARISH,
    keepSignalState: true
  })
});

export const SignalConfigSchema = z.union([
  RsiSignalConfigSchema,
  MaSignalConfigSchema,
  ValueSignalConfigSchema,
  LiquidationSignalConfigSchema
]);

const SignalConfluenceConfigSchema = z.object({
  // Builder/editor may initialize only one side first (e.g. entries),
  // so we default missing arrays to keep payload valid while user is editing.
  entries: z.array(SignalConfluenceQuerySchema).default([]),
  exits: z.array(SignalConfluenceQuerySchema).default([])
});

export const StrategyConfigInputSchema = z.object({
  value: z
    .object({
      long: SignalConfluenceConfigSchema.optional(),
      short: SignalConfluenceConfigSchema.optional()
    })
    .optional(),
  plot: z.boolean().optional()
});

export type SignalCondition = z.infer<typeof SignalConfluenceQuerySchema>;
export type SignalConfig = z.infer<typeof SignalConfigSchema>;
export type SignalParams = SignalConfig['params'];
export type SignalConfluenceConfig = z.infer<
  typeof SignalConfluenceConfigSchema
>;
export type StrategyConfigInput = z.infer<typeof StrategyConfigInputSchema>;
